import React, { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Download, Printer, ChevronDown, ArrowUpDown } from "lucide-react";

import { reconciliationService } from "../../../services/reportService";
import { SearchInput, Segmented, Button } from "../../components/MasterUi";

/* ================= helpers ================= */

// angka dari backend sudah diformat "1.234" / "-1.234" -> number
const toNum = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "number") return v;
    const n = Number(String(v).replace(/\./g, "").replace(",", "."));
    return Number.isNaN(n) ? 0 : n;
};

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtDate = (s) => {
    if (!s || s === "-") return "-";
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${Number(m[3])} ${BULAN[Number(m[2]) - 1]} ${m[1]}` : String(s);
};

const PER_PAGE = 50;

const STATUS_OPTIONS = [
    { value: "", label: "Semua" },
    { value: "SELISIH", label: "Selisih" },
    { value: "COCOK", label: "Cocok" }
];

const SORT_OPTIONS = [
    { value: "default", label: "Urutan asli" },
    { value: "diff_desc", label: "Selisih terbesar" },
    { value: "item", label: "Kode item" }
];

/* ================= kartu ringkasan ================= */

function SummaryTile({ label, value, sub, tone = "default", active, onClick }) {
    const tones = {
        default: "text-body",
        ok: "text-emerald-700",
        bad: "text-red-700"
    };
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            aria-pressed={onClick ? active : undefined}
            className={`text-left bg-white rounded-lg border p-4 transition-colors ${
                active ? "border-ink ring-2 ring-ink/15" : "border-line"
            } ${onClick ? "hover:border-ink/60" : ""}`}
        >
            <div className="text-sm text-muted">{label}</div>
            <div className={`text-[28px] leading-none font-semibold tabular-nums mt-2 ${tones[tone]}`}>{value}</div>
            {sub && <div className="text-xs text-muted mt-1.5">{sub}</div>}
        </Tag>
    );
}

/* ================= selisih ================= */

function DiffCell({ value }) {
    if (value === 0) return <span className="text-muted tabular-nums">0</span>;
    const oracleMore = value > 0; // selisih = Oracle - WMS
    return (
        <span className="inline-flex flex-col items-end leading-tight">
            <span className="font-semibold text-red-700 tabular-nums">
                {oracleMore ? "+" : "−"}{fmt(Math.abs(value))}
            </span>
            <span className="text-[11px] text-muted whitespace-nowrap">
                {oracleMore ? "Oracle lebih banyak" : "WMS lebih banyak"}
            </span>
        </span>
    );
}

function StatusPill({ status }) {
    const ok = status === "COCOK";
    return (
        <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 ${
                ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
            {ok ? "Cocok" : "Selisih"}
        </span>
    );
}

/* ================= halaman ================= */

export default function ReconciliationReport() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [loadedAt, setLoadedAt] = useState(null);

    const [warehouses, setWarehouses] = useState([]);
    const [warehouseId, setWarehouseId] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [sortBy, setSortBy] = useState("default");
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState({});

    const requestId = useRef(0);

    // daftar warehouse
    useEffect(() => {
        reconciliationService
            .getWarehouses()
            .then((res) => setWarehouses(res.data?.data || []))
            .catch(() => setWarehouses([]));
    }, []);

    const fetchData = () => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        reconciliationService
            .getReconciliationReport(warehouseId ? { warehouse_id: warehouseId } : {})
            .then((res) => {
                if (id !== requestId.current) return;
                if (res.data?.success) {
                    setRows(res.data.data || []);
                    setLastRefresh(res.data.last_refresh_date || null);
                    setLoadedAt(new Date());
                } else {
                    setRows([]);
                    setError(res.data?.message || "Data rekonsiliasi tidak bisa diambil.");
                }
            })
            .catch((err) => {
                if (id !== requestId.current) return;
                setRows([]);
                setError(
                    err.response?.data?.message ||
                    "Data rekonsiliasi tidak bisa diambil. Periksa koneksi ke server lalu coba lagi."
                );
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    };

    useEffect(() => {
        fetchData();
        setExpanded({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseId]);

    useEffect(() => {
        setPage(1);
    }, [warehouseId, search, status, sortBy]);

    /* ===== olah data ===== */

    const enriched = useMemo(
        () =>
            rows.map((r, i) => ({
                ...r,
                _key: `${r.kode_item}|${r.no_lot}|${i}`,
                _oracle: toNum(r.qty_oracle),
                _wms: toNum(r.qty_wms),
                _diff: toNum(r.selisih)
            })),
        [rows]
    );

    const summary = useMemo(() => {
        const total = enriched.length;
        const mismatch = enriched.filter((r) => r.status === "SELISIH");
        const oracleMore = mismatch.filter((r) => r._diff > 0);
        const wmsMore = mismatch.filter((r) => r._diff < 0);
        return {
            total,
            match: total - mismatch.length,
            mismatch: mismatch.length,
            accuracy: total ? ((total - mismatch.length) / total) * 100 : null,
            oracleMoreCount: oracleMore.length,
            oracleMoreQty: oracleMore.reduce((a, r) => a + r._diff, 0),
            wmsMoreCount: wmsMore.length,
            wmsMoreQty: wmsMore.reduce((a, r) => a + Math.abs(r._diff), 0)
        };
    }, [enriched]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = enriched.filter((r) => {
            if (status && r.status !== status) return false;
            if (!q) return true;
            return [r.kode_item, r.nama_item, r.no_lot].some((v) => String(v ?? "").toLowerCase().includes(q));
        });
        if (sortBy === "diff_desc") list = [...list].sort((a, b) => Math.abs(b._diff) - Math.abs(a._diff));
        if (sortBy === "item") list = [...list].sort((a, b) => String(a.kode_item).localeCompare(String(b.kode_item)));
        return list;
    }, [enriched, search, status, sortBy]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const currentPage = Math.min(page, totalPages);

    const warehouseName = warehouseId
        ? warehouses.find((w) => String(w.id) === String(warehouseId))?.name || "Warehouse"
        : "Semua warehouse";

    /* ===== export & print ===== */

    const handleExport = () => {
        const headers = ["Kode Item", "Nama Item", "No. Lot", "ED", "Qty Oracle", "Qty WMS", "Selisih", "Status", "Warehouse", "Tanggal Masuk Rak"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = [headers.join(",")];
        filtered.forEach((r) => {
            lines.push([
                esc(r.kode_item), esc(r.nama_item), esc(r.no_lot), esc(r.ed),
                r._oracle, r._wms, r._diff, esc(r.status), esc(r.warehouse), esc(r.created_at)
            ].join(","));
        });
        const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const d = new Date();
        a.href = url;
        a.download = `rekonsiliasi_stok_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const thClass = "px-4 py-2.5 text-xs font-medium text-muted bg-surface border-b border-line whitespace-nowrap";
    const tdClass = "px-4 py-3 border-b border-line text-sm align-middle";

    return (
        <div className="max-w-[1600px] mx-auto space-y-5 print:space-y-3">

            {/* ===== judul ===== */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-semibold text-body tracking-tight">Rekonsiliasi stok</h2>
                    <p className="text-sm text-muted mt-1">
                        Perbandingan qty Oracle dan qty di rak WMS per item dan lot, {warehouseName}
                    </p>
                    <p className="text-xs text-muted mt-1">
                        Data Oracle terakhir diperbarui{" "}
                        <span className="font-semibold text-body">{lastRefresh || "tidak diketahui"}</span>
                        {loadedAt && (
                            <>
                                , halaman dimuat{" "}
                                {loadedAt.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }).replace(".", ":")} WIB
                            </>
                        )}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 print:hidden">
                    <Button onClick={fetchData} disabled={loading}>
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> {loading ? "Memuat…" : "Muat ulang"}
                    </Button>
                    <Button onClick={handleExport} disabled={!filtered.length}>
                        <Download size={16} /> Export CSV
                    </Button>
                    <Button onClick={() => window.print()} disabled={!filtered.length}>
                        <Printer size={16} /> Cetak
                    </Button>
                </div>
            </div>

            {/* ===== ringkasan ===== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryTile
                    label="Item-lot dibandingkan"
                    value={fmt(summary.total)}
                    sub={summary.accuracy !== null ? `Akurasi ${summary.accuracy.toLocaleString("id-ID", { maximumFractionDigits: 1 })}%` : "—"}
                    active={status === ""}
                    onClick={() => setStatus("")}
                />
                <SummaryTile
                    label="Cocok"
                    value={fmt(summary.match)}
                    sub="qty Oracle = qty WMS"
                    tone="ok"
                    active={status === "COCOK"}
                    onClick={() => setStatus("COCOK")}
                />
                <SummaryTile
                    label="Selisih"
                    value={fmt(summary.mismatch)}
                    sub="perlu dicek"
                    tone={summary.mismatch ? "bad" : "default"}
                    active={status === "SELISIH"}
                    onClick={() => setStatus("SELISIH")}
                />
                <div className="bg-white rounded-lg border border-line p-4">
                    <div className="text-sm text-muted">Arah selisih</div>
                    <dl className="mt-2 space-y-1.5 text-sm">
                        <div className="flex justify-between gap-3">
                            <dt className="text-muted">Oracle lebih banyak</dt>
                            <dd className="tabular-nums">
                                <span className="font-semibold text-body">{fmt(summary.oracleMoreCount)}</span>
                                <span className="text-muted"> lot, +{fmt(summary.oracleMoreQty)}</span>
                            </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="text-muted">WMS lebih banyak</dt>
                            <dd className="tabular-nums">
                                <span className="font-semibold text-body">{fmt(summary.wmsMoreCount)}</span>
                                <span className="text-muted"> lot, −{fmt(summary.wmsMoreQty)}</span>
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* ===== tabel ===== */}
            <section className="bg-white rounded-lg border border-line print:border-0">

                {/* toolbar */}
                <div className="flex flex-col xl:flex-row xl:items-center gap-3 px-5 py-4 border-b border-line print:hidden">
                    <select
                        value={warehouseId}
                        onChange={(e) => setWarehouseId(e.target.value)}
                        aria-label="Warehouse"
                        className="h-10 xl:w-56 bg-white border border-line rounded-md px-3 text-sm text-body focus:border-ink focus:ring-0"
                    >
                        <option value="">Semua warehouse</option>
                        {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                    </select>

                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder="Cari kode item, nama item, atau lot"
                        className="xl:w-80"
                    />

                    <div className="flex flex-wrap items-center gap-3 xl:ml-auto">
                        <Segmented label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
                        <label className="flex items-center gap-2 text-sm text-muted">
                            <ArrowUpDown size={15} />
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                aria-label="Urutkan"
                                className="h-10 bg-white border border-line rounded-md px-3 text-sm text-body focus:border-ink focus:ring-0"
                            >
                                {SORT_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                <div className="px-5 py-2.5 text-xs text-muted border-b border-line print:hidden" aria-live="polite">
                    {loading
                        ? "Memuat data…"
                        : `${fmt(filtered.length)} dari ${fmt(summary.total)} item-lot ditampilkan. Selisih dihitung qty Oracle dikurangi qty WMS.`}
                </div>

                {error ? (
                    <div className="m-5 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">{error}</div>
                ) : (
                    <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-50" : ""}`}>
                        <table className="w-full border-collapse min-w-[900px]">
                            <thead>
                                <tr>
                                    <th className={`${thClass} w-8 print:hidden`} aria-label="Detail" />
                                    <th className={`${thClass} text-left`}>Item</th>
                                    <th className={`${thClass} text-left`}>Lot</th>
                                    <th className={`${thClass} text-left`}>ED</th>
                                    <th className={`${thClass} text-right`}>Qty Oracle</th>
                                    <th className={`${thClass} text-right`}>Qty WMS</th>
                                    <th className={`${thClass} text-right`}>Selisih</th>
                                    <th className={`${thClass} text-left`}>Status</th>
                                    <th className={`${thClass} text-left`}>Warehouse</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* layar: per halaman; cetak: semua baris hasil filter */}
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-4 py-14 text-center text-sm text-muted">
                                            {loading
                                                ? "Memuat data rekonsiliasi…"
                                                : search || status
                                                    ? "Tidak ada item yang cocok dengan filter ini. Ubah kata kunci atau status."
                                                    : "Belum ada stok di rak untuk dibandingkan."}
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((r, i) => {
                                        const onPage = i >= (currentPage - 1) * PER_PAGE && i < currentPage * PER_PAGE;
                                        const isOpen = Boolean(expanded[r._key]);
                                        const hasRacks = r.rack_details?.length > 0;
                                        const mismatch = r.status === "SELISIH";
                                        return (
                                            <React.Fragment key={r._key}>
                                                <tr
                                                    className={`${onPage ? "" : "hidden print:table-row"} ${
                                                        mismatch ? "bg-red-50/30" : ""
                                                    } hover:bg-surface/70 ${hasRacks ? "cursor-pointer" : ""}`}
                                                    onClick={hasRacks ? () => setExpanded((p) => ({ ...p, [r._key]: !p[r._key] })) : undefined}
                                                >
                                                    <td className={`${tdClass} print:hidden ${mismatch ? "border-l-2 border-l-red-400" : "border-l-2 border-l-transparent"}`}>
                                                        {hasRacks && (
                                                            <ChevronDown
                                                                size={16}
                                                                className={`text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
                                                                aria-label={isOpen ? "Tutup detail rak" : "Lihat detail rak"}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className={tdClass}>
                                                        <div className="font-semibold text-body tabular-nums">{r.kode_item}</div>
                                                        <div className="text-xs text-muted max-w-[260px] truncate" title={r.nama_item}>{r.nama_item}</div>
                                                    </td>
                                                    <td className={`${tdClass} tabular-nums whitespace-nowrap`}>{r.no_lot}</td>
                                                    <td className={`${tdClass} tabular-nums whitespace-nowrap`}>{fmtDate(r.ed)}</td>
                                                    <td className={`${tdClass} text-right tabular-nums`}>{fmt(r._oracle)}</td>
                                                    <td className={`${tdClass} text-right tabular-nums`}>{fmt(r._wms)}</td>
                                                    <td className={`${tdClass} text-right`}><DiffCell value={r._diff} /></td>
                                                    <td className={tdClass}><StatusPill status={r.status} /></td>
                                                    <td className={`${tdClass} text-muted whitespace-nowrap`}>{r.warehouse || "-"}</td>
                                                </tr>

                                                {isOpen && onPage && (
                                                    <tr className="print:hidden">
                                                        <td colSpan={9} className="bg-surface/60 border-b border-line pl-12 pr-4 py-3">
                                                            <div className="rounded-md border border-line bg-white overflow-x-auto">
                                                                <div className="px-3 py-2 text-xs text-muted border-b border-line">
                                                                    Lokasi di rak: {r.rack_details.length} baris, total {fmt(r._wms)}
                                                                </div>
                                                                <table className="w-full text-sm">
                                                                    <thead>
                                                                        <tr className="text-xs text-muted">
                                                                            <th className="text-left font-medium px-3 py-1.5">Rak</th>
                                                                            <th className="text-left font-medium px-3 py-1.5">Slot</th>
                                                                            <th className="text-left font-medium px-3 py-1.5">Palet</th>
                                                                            <th className="text-left font-medium px-3 py-1.5">Masuk rak</th>
                                                                            <th className="text-right font-medium px-3 py-1.5">Qty</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {r.rack_details.map((d, j) => (
                                                                            <tr key={j} className="border-t border-line">
                                                                                <td className="px-3 py-1.5 font-medium">{d.nama_rak}</td>
                                                                                <td className="px-3 py-1.5 tabular-nums">{d.kode_level}</td>
                                                                                <td className="px-3 py-1.5">
                                                                                    {d.kode_palet ? (
                                                                                        <span className="inline-block px-1.5 py-0.5 rounded bg-ink-100 text-ink text-xs font-semibold">{d.kode_palet}</span>
                                                                                    ) : (
                                                                                        <span className="text-muted">-</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-3 py-1.5 text-muted tabular-nums">{d.created_at || "-"}</td>
                                                                                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{d.qty}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* pagination */}
                {!error && filtered.length > PER_PAGE && (
                    <div className="flex items-center justify-between gap-3 px-5 py-3 text-sm print:hidden">
                        <span className="text-muted">
                            {fmt((currentPage - 1) * PER_PAGE + 1)}–{fmt(Math.min(currentPage * PER_PAGE, filtered.length))} dari {fmt(filtered.length)}
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-muted mr-1 hidden sm:inline">Halaman {currentPage} dari {totalPages}</span>
                            <button
                                type="button"
                                disabled={currentPage <= 1}
                                onClick={() => setPage(currentPage - 1)}
                                className="h-8 px-3 border border-line rounded-md bg-white hover:bg-surface disabled:opacity-40"
                            >
                                Sebelumnya
                            </button>
                            <button
                                type="button"
                                disabled={currentPage >= totalPages}
                                onClick={() => setPage(currentPage + 1)}
                                className="h-8 px-3 border border-line rounded-md bg-white hover:bg-surface disabled:opacity-40"
                            >
                                Berikutnya
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
